import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Modal, Pagination } from 'react-bootstrap';
import { FiUsers, FiRefreshCw, FiMail, FiPhone, FiBriefcase, FiPlus, FiTrash2, FiEye, FiEdit } from 'react-icons/fi';
import { getAdminUsers, deleteAdminUser } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminUsers.css';

const ITEMS_PER_PAGE = 5;
const MAX_VISIBLE_PAGES = 5;

const AdminUsers = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);

    const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;
    const indexOfLastItem = indexOfFirstItem + users.length;

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAdminUsers({
                skip: (currentPage - 1) * ITEMS_PER_PAGE,
                limit: ITEMS_PER_PAGE,
            });
            // API returns { users: [...], meta: { total, skip, limit, page, total_pages } }
            const list = Array.isArray(data?.users) ? data.users : [];
            const total = Number(data?.meta?.total);
            setUsers(list);
            setTotalItems(Number.isFinite(total) ? total : list.length);
        } catch (e) {
            setError(handleApiError(e, t('pages.adminUsers.errorLoading')));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        if (!loading && currentPage > totalPages) setCurrentPage(totalPages);
    }, [loading, currentPage, totalPages]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages || pageNumber === currentPage) return;
        setCurrentPage(pageNumber);
    };

    const getPaginationRange = () => {
        let startPage = Math.max(1, currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
        const endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);
        if (endPage - startPage < MAX_VISIBLE_PAGES - 1) {
            startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
        }
        return { startPage, endPage };
    };

    const renderPaginationItems = () => {
        const { startPage, endPage } = getPaginationRange();
        const items = [];

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>
            );
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleDeleteClick = (e, user) => {
        e.stopPropagation(); // Empêcher le clic sur la ligne
        setUserToDelete(user);
        setShowDeleteModal(true);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        try {
            setDeleteLoading(true);
            setDeleteError(null);
            await deleteAdminUser(userToDelete.user_uuid);
            // Recharger la liste après suppression
            await fetchUsers();
            setShowDeleteModal(false);
            setUserToDelete(null);
        } catch (err) {
            setDeleteError(handleApiError(err, t('pages.adminUsers.errorDeleting')));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteError(null);
    };

    return (
        <div className="dashboard-page admin-users-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiUsers className="me-2" />
                                {t('pages.adminUsers.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.adminUsers.subtitle')}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                onClick={() => navigate('/admin/users/create')}
                                disabled={loading}
                            >
                                <FiPlus className="me-1" /> {t('pages.adminUsers.createUser')}
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={() => fetchUsers()}
                                disabled={loading}
                            >
                                <FiRefreshCw className={loading ? 'spinning' : ''} /> {t('pages.adminUsers.refresh')}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}

                    <Card className="mt-4">
                        <Card.Header>
                            <h5 className="mb-0">{t('pages.adminUsers.userList')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3">{t('pages.adminUsers.loadingUsers')}</p>
                                </div>
                            ) : users.length === 0 ? (
                                <div className="text-center py-5">
                                    <FiUsers size={48} className="text-muted mb-3" />
                                    <p className="text-muted">{t('pages.adminUsers.noUsersFound')}</p>
                                </div>
                            ) : (
                                <div className="table-responsive table-scroll">
                                    <Table hover className="admin-table admin-users-table">
                                        <thead>
                                            <tr>
                                                <th>{t('pages.adminUsers.fullName')}</th>
                                                <th>{t('pages.adminUsers.email')}</th>
                                                <th>{t('pages.adminUsers.phone')}</th>
                                                <th>{t('pages.adminUsers.company')}</th>
                                                <th className="text-end col-actions">{t('pages.adminUsers.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.user_uuid}>
                                                    <td title={`${user.first_name || ''} ${user.last_name || ''}`.trim()}>
                                                        <strong>{user.first_name || ''} {user.last_name || ''}</strong>
                                                    </td>
                                                    <td title={user.email || ''}>
                                                        {user.email ? (
                                                            <>
                                                                <FiMail className="me-1" />
                                                                {user.email}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.phone_number ? (
                                                            <>
                                                                <FiPhone className="me-1" />
                                                                {user.phone_number}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td title={user.company_name || ''}>
                                                        {user.company_name ? (
                                                            <>
                                                                <FiBriefcase className="me-1" />
                                                                {user.company_name}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end col-actions">
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => navigate(`/admin/users/${user.user_uuid}`)}
                                                            title={t('pages.adminUsers.viewDetails')}
                                                        >
                                                            <FiEye />
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => navigate(`/admin/users/edit/${user.user_uuid}`)}
                                                            title={t('pages.adminUsers.edit')}
                                                        >
                                                            <FiEdit />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={(e) => handleDeleteClick(e, user)}
                                                            title={t('pages.adminUsers.delete')}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            {!loading && totalItems > 0 && (
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4 partner-pagination-bar">
                                    <div className="text-muted">
                                        Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} utilisateur{totalItems > 1 ? 's' : ''} admin
                                    </div>
                                    {totalPages > 1 && (
                                        <Pagination className="mb-0">
                                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                            {renderPaginationItems()}
                                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                        </Pagination>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                {t('pages.adminUsers.confirmDelete')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteError && (
                                <Alert variant="danger" className="mb-3">
                                    <strong>{t('common.error')} :</strong> {deleteError}
                                </Alert>
                            )}
                            {userToDelete && (
                                <>
                                    <p dangerouslySetInnerHTML={{ __html: t('pages.adminUsers.deleteMessage', { name: `${userToDelete.first_name} ${userToDelete.last_name}` }) }} />
                                    <p className="text-muted">
                                        <small>{t('pages.adminUsers.deleteWarning')}</small>
                                    </p>
                                    {userToDelete.email && (
                                        <div className="mt-3 p-2 bg-light rounded">
                                            <small>
                                                <strong>{t('pages.adminUsers.email')} :</strong> {userToDelete.email}
                                            </small>
                                        </div>
                                    )}
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseDeleteModal}
                                disabled={deleteLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteConfirm}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminUsers.deleting')}
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        {t('pages.adminUsers.confirmDeleteButton')}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminUsers;
